import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { TherapyTool, accessEndsAt, centerState } from "../../domain/types";
import AdoptButton from "./AdoptButton";
import CenterManagedNotice from "./CenterManagedNotice";
import { toolName, toolShortDescription } from "../toolCopy";
import { formatDay, toolPriceLabel } from "../price";
import { useBillingPeriod } from "../../../subscription";
import { iconFor } from "../toolIcons";

interface ToolRowProps {
    tool: TherapyTool;
}

/**
 * One catalogue entry as a line, for running an eye down the whole catalogue.
 *
 * The same facts as `ToolCard` — name, one line of description, price, whether
 * it is theirs, the action — without the labels, which are what the filters
 * are for. On a phone the action drops under the name rather than squeezing it.
 */
const ToolRow: React.FC<ToolRowProps> = ({ tool }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const language = i18n.language || "es";

    const Icon = iconFor(tool.icon);
    const endsAt = accessEndsAt(tool);
    const period = useBillingPeriod();
    const priceLabel = toolPriceLabel(t, tool, language, period);

    return (
        <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-800 dark:bg-gray-900 sm:flex-row sm:items-center">
            <button
                type="button"
                onClick={() => navigate(`/tools-catalog/${tool.slug}`)}
                className="flex min-w-0 flex-1 items-center gap-3 text-left"
            >
                <span
                    aria-hidden="true"
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400"
                >
                    <Icon className="h-4 w-4" />
                </span>
                <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-content-primary">
                        {toolName(t, tool)}
                    </span>
                    <span className="block truncate text-xs text-content-tertiary">
                        {toolShortDescription(t, tool)}
                    </span>
                </span>
            </button>

            <div className="flex flex-wrap items-center gap-3 sm:shrink-0 sm:justify-end">
                <span className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-600 dark:bg-brand-500/10 dark:text-brand-400">
                    {priceLabel}
                </span>
                {tool.disabled_by_center || tool.managed_by_center ? (
                    <CenterManagedNotice compact state={centerState(tool)} />
                ) : (
                    tool.adopted && (
                        <span className="text-xs font-medium text-success-600 dark:text-success-400">
                            {endsAt
                                ? t("marketplace.adoptedUntil", {
                                      date: formatDay(endsAt, language),
                                  })
                                : t("marketplace.adopted")}
                        </span>
                    )
                )}
                <AdoptButton tool={tool} />
            </div>
        </div>
    );
};

export default ToolRow;
